/**
 * Export and import, because localStorage is one accidental "clear browsing
 * data" away from an empty fridge and there is no server to fall back on.
 *
 * It is also the migration path: when accounts arrive, this file is already
 * the shape the sync layer has to speak.
 *
 * Deliberately *not* a straight dump of localStorage. A backup is a document
 * with a version on it, so a file written today can still be read after the
 * storage keys change.
 */

import { FOOD_CATEGORIES, STORAGE_LOCATIONS, UNITS } from "../food/types";
import type { FoodItem } from "../food/types";
import { loadSettings, saveSettings } from "../settings/settingsStorage";
import type { Settings } from "../settings/types";
import { loadCart, saveCart } from "../cart/cartStorage";
import type { CartLine } from "../cart/types";
import { loadFoodItems, replaceFoodItems } from "../../lib/storage/foodStorage";

/** Bumped only when the shape changes in a way a reader must know about. */
export const BACKUP_VERSION = 1;

export interface Backup {
  app: "pantrymind";
  version: number;
  exportedAt: string;
  items: FoodItem[];
  settings: Settings;
  /**
   * The shopping list. Optional so a v1 file written before this existed still
   * imports cleanly — an older backup simply has no list, which is true.
   */
  cart?: CartLine[];
}

export interface ImportResult {
  /** Items actually written. */
  imported: number;
  /** Entries in the file that weren't usable items. */
  skipped: number;
  /** True when the file's settings were applied too. */
  settingsApplied: boolean;
  /** Shopping-list lines restored. */
  cartRestored: number;
}

export class BackupError extends Error {
  name = "BackupError";
}

/** Everything worth keeping, as a document. */
export function exportBackup(): Backup {
  return {
    app: "pantrymind",
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    items: loadFoodItems(),
    settings: loadSettings(),
    cart: loadCart(),
  };
}

/** A filename with the date in it, so a folder of these stays sortable. */
export function backupFilename(today: string): string {
  return `pantrymind-${today}.json`;
}

const oneOf = (list: readonly string[], value: unknown) =>
  typeof value === "string" && list.includes(value);

/**
 * Photos must be inline image data — never a URL.
 *
 * Every other field was checked against a known list; `photo` went straight
 * through into an `<img src>`. A backup file from someone else could point it
 * at their server, and opening your own inventory would then quietly tell them
 * you had opened it. Our own camera only ever produces `data:image/`, so this
 * costs nothing real.
 */
const MAX_PHOTO_BYTES = 400_000;

function isOwnPhoto(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.startsWith("data:image/") &&
    value.length <= MAX_PHOTO_BYTES
  );
}

/**
 * True when an entry has enough of an item to be worth keeping.
 *
 * Stricter than `loadFoodItems`, which only checks for an id: that function is
 * reading data this app wrote, while this one is reading a file that could be
 * anything, including a different app's export.
 */
function isItem(entry: unknown): entry is FoodItem {
  if (typeof entry !== "object" || entry === null) return false;
  const item = entry as Record<string, unknown>;

  return (
    typeof item.id === "string" &&
    item.id.length > 0 &&
    typeof item.name === "string" &&
    item.name.trim().length > 0 &&
    oneOf(FOOD_CATEGORIES, item.category) &&
    oneOf(STORAGE_LOCATIONS, item.location) &&
    oneOf(UNITS, item.unit) &&
    typeof item.quantity === "number" &&
    Number.isFinite(item.quantity) &&
    typeof item.purchaseDate === "string" &&
    typeof item.estimatedExpiryDate === "string" &&
    (item.photo === undefined || isOwnPhoto(item.photo))
  );
}

/**
 * Reads a backup file and replaces what's stored.
 *
 * Replaces rather than merges. Merging two inventories means deciding what to
 * do about the same id with different contents, and every answer to that is a
 * guess; "this file is now your fridge" is at least predictable. The UI asks
 * first.
 *
 * Throws only when the file isn't a PantryMind backup at all. A file with some
 * unusable rows imports the rest and reports the count, the same way the
 * storage layer already survives one corrupt record.
 */
export function importBackup(text: string): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new BackupError("That file isn't JSON.");
  }

  if (typeof parsed !== "object" || parsed === null) {
    throw new BackupError("That file isn't a PantryMind backup.");
  }

  const backup = parsed as Record<string, unknown>;
  if (backup.app !== "pantrymind") {
    throw new BackupError("That file isn't a PantryMind backup.");
  }
  if (typeof backup.version !== "number" || backup.version > BACKUP_VERSION) {
    throw new BackupError(
      "That backup was written by a newer version of PantryMind than this one."
    );
  }
  if (!Array.isArray(backup.items)) {
    throw new BackupError("That backup has no items in it.");
  }

  const items = backup.items.filter(isItem);
  const skipped = backup.items.length - items.length;

  replaceFoodItems(items);

  // Settings are a bonus, never a reason to fail: a backup that restores the
  // food but not the theme is still a good outcome.
  let settingsApplied = false;
  if (typeof backup.settings === "object" && backup.settings !== null) {
    try {
      saveSettings({ ...loadSettings(), ...(backup.settings as Partial<Settings>) } as Settings);
      // Round-trip through the reader, which validates field by field, so an
      // unrecognised value from the file doesn't stay in storage.
      saveSettings(loadSettings());
      settingsApplied = true;
    } catch {
      settingsApplied = false;
    }
  }

  /*
   * The list, restored the same way settings are: a bonus, never a reason to
   * fail. Someone importing a backup wants their food back; losing the whole
   * restore because a shopping list was malformed would be the tail wagging the
   * dog.
   *
   * Written through saveCart and read back, so every line goes through the same
   * validation a stored list does and nothing unvalidated from a file lands in
   * storage.
   */
  let cartRestored = 0;
  if (Array.isArray(backup.cart)) {
    try {
      saveCart(backup.cart as CartLine[]);
      cartRestored = loadCart().length;
    } catch {
      cartRestored = 0;
    }
  }

  return { imported: items.length, skipped, settingsApplied, cartRestored };
}
